import React, { FunctionComponent, useState } from 'react';
import { Formik, FormikProps } from 'formik';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { ISelectedPosition } from '../../../../common/models/position/ISelectedPosition';
import {
  getSelectedContactsColumns,
  isWarningShown,
  bothChangedModalText,
  emailChangedModalText,
  phoneChangedModalText
} from './utils';
import { IAppState } from '../../../../common/models/store/IAppState';
import Table from '../../../../components/Table';
import {
  addReqBuilderCandidateRoutine,
  removeReqBuilderCandidateRoutine,
  updateSelectedCandidateRoutine
} from '../../routines';
import { IBindingCallback1 } from '../../../../common/models/callback';
import { IRefReqSelectedContact } from '../../../../common/models/contact/IRefReqSelectedContact';
import { editContactValidationSchema, reqBuilderStep2ValidationSchema } from '../../../../common/validation/yup';
import Step2Form from '../Step2Form';
import ConfirmationModal from '../../../../components/ConfirmationModal';
import { IFormattedMessage } from '../../../../common/models/common/IFormattedMessage';
import { errorToast } from '../../../../common/helpers/toastrHelper';
import { INewContactInfo } from '../../../../common/models/contact/INewContactInfo';
import { initialFormValues, trimValues } from '../../components/AddContactForm/utils';

interface IProps {
  selectedPosition: ISelectedPosition | null;
  candidates: IRefReqSelectedContact[];
  references: INewContactInfo[] | null;
  addCandidate: IBindingCallback1<IRefReqSelectedContact>;
  removeCandidate: IBindingCallback1<IRefReqSelectedContact>;
  updateCandidate: IBindingCallback1<{ index: number; data: INewContactInfo; id?: string }>;
}

interface IEditCandidate {
  index: number;
  values: INewContactInfo;
  prevData: IRefReqSelectedContact;
}

const Step2: FunctionComponent<IProps> = ({
  selectedPosition,
  candidates,
  references,
  removeCandidate,
  updateCandidate,
  addCandidate
}) => {
  const [editCandidate, setEditCandidate] = useState<IEditCandidate | undefined>();
  const [addCandidateValues, setAddCandidateValues] = useState<INewContactInfo | undefined>();

  const checkAndAddCandidate = (data: IRefReqSelectedContact) => {
    if (candidates.length && references) {
      setAddCandidateValues(data);
    } else {
      addCandidate(data);
    }
  };

  const onAddCandidate = (values: INewContactInfo) => {
    const candidate = trimValues(values);
    checkAndAddCandidate(candidate);
  };

  const confirmAddCandidate = () => {
    addCandidate(addCandidateValues as INewContactInfo);
    setAddCandidateValues(undefined);
  };

  const onUpdate = (index: number, values: INewContactInfo) => {
    try {
      editContactValidationSchema.validateSync(values);
      const candidateData = candidates[index];
      const isDataChanged = candidateData.email !== values.email
        || candidateData.phoneNumber !== values.phoneNumber;

      if (candidateData.id && isDataChanged) {
        setEditCandidate({ index, values, prevData: candidateData });
      } else {
        updateCandidate({ index, data: values, id: candidateData.id });
      }
    } catch (error) {
      errorToast(error.message);
    }
  };

  const getSelectedColumns = () => getSelectedContactsColumns({
    withWarning: isWarningShown(candidates),
    position: selectedPosition?.name as string,
    actions: { onDelete: removeCandidate, onEdit: onUpdate }
  });

  const getEditModalTexts = (): { titleText: IFormattedMessage; descriptionText: IFormattedMessage } => {
    const { values, prevData } = editCandidate as IEditCandidate;
    const isPhoneChanged = values.phoneNumber !== prevData.phoneNumber;
    const isEmailChanged = values.email !== prevData.email;
    const intlValues = {
      firstName: prevData.firstName,
      lastName: prevData.lastName,
      indent: (
        <>
          <br />
          <br />
        </>
      ),
      newLabel: <ins><FormattedMessage id="new" defaultMessage="New" /></ins>
    };
    let intlData;

    if (isEmailChanged && isPhoneChanged) {
      intlData = bothChangedModalText;
    } else {
      intlData = isEmailChanged ? emailChangedModalText : phoneChangedModalText;
    }

    const { titleText, descriptionText } = intlData;
    return {
      titleText: { ...titleText, values: intlValues },
      descriptionText: { ...descriptionText, values: intlValues }
    };
  };

  const onConfirmChange = () => {
    const { index, values, prevData } = editCandidate as IEditCandidate;
    updateCandidate({
      index,
      data: values,
      id: prevData.id
    });
    setEditCandidate(undefined);
  };

  return (
    <Formik
      validationSchema={reqBuilderStep2ValidationSchema}
      initialValues={initialFormValues}
      onSubmit={onAddCandidate}
    >
      {(props: FormikProps<INewContactInfo>) => (
        <div className="add-contact-step">
          <header className="add-contact-step-header">
            <FormattedMessage id="step2.description" defaultMessage="Add your candidates here" />
            <Step2Form {...props} addCandidate={checkAndAddCandidate} />
          </header>
          <main className="add-contact-step-body">
            <h1 className="add-contact-step-body-header">
              <FormattedMessage
                id="step2.selectedCandidates"
                defaultMessage="Selected candidates"
                values={{ count: candidates.length }}
              />
              {Boolean(candidates.length) && <span className="add-contact-step-count">{candidates.length}</span>}
            </h1>
            {candidates.length
              ? (
                <Table
                  config={{
                    data: { items: candidates, total: candidates.length },
                    columns: getSelectedColumns(),
                    onCellClicked: (_origin, event) => event.stopPropagation()
                  }}
                />
              )
              : (
                <span className="add-contact-step-placeholder">
                  <FormattedMessage id="step2.noCandidates" defaultMessage="Candidates selected will be shown here." />
                </span>
              )}
          </main>
          {editCandidate && (
            <ConfirmationModal
              isShown={Boolean(editCandidate)}
              {...getEditModalTexts()}
              confirmBttnText={{ id: 'step2.confirmChange.confirm', defaultMessage: 'Yes, overwrite' }}
              cancelBttnText={{ id: 'goBack', defaultMessage: 'No, go back' }}
              onConfirm={onConfirmChange}
              onCancel={() => setEditCandidate(undefined)}
            />
          )}
          {addCandidateValues && (
            <ConfirmationModal
              isShown={Boolean(addCandidateValues)}
              titleText={{
                id: 'step2.confirmAdd.title',
                defaultMessage: 'Add new candidate?'
              }}
              descriptionText={{
                id: 'step2.confirmAdd.description',
                defaultMessage: `Adding new candidate will reset created references for current candidate. 
                If you don't want to lose references' data, it is better to create new reference request.`
              }}
              confirmBttnText={{ id: 'step2.confirmAdd.confirm', defaultMessage: 'Yes, add' }}
              cancelBttnText={{ id: 'goBack', defaultMessage: 'No, go back' }}
              onConfirm={confirmAddCandidate}
              onCancel={() => setAddCandidateValues(undefined)}
            />
          )}
        </div>
      )}
    </Formik>
  );
};

const mapStateToProps = ({
  requestBuilder: { selectedPosition, candidates, references }
}: IAppState) => ({ selectedPosition, candidates, references });

const mapDispatchToProps = {
  addCandidate: addReqBuilderCandidateRoutine,
  removeCandidate: removeReqBuilderCandidateRoutine,
  updateCandidate: updateSelectedCandidateRoutine
};

export default connect(mapStateToProps, mapDispatchToProps)(Step2);
